import React, { useState } from 'react';
import { 
  Activity, 
  FileText, 
  ShieldCheck,
  Send
} from 'lucide-react';
import { GoogleGenAI } from '@google/genai';
import ReactMarkdown from 'react-markdown';

interface LabAnalysisProps {
  isAuthenticated: boolean;
}

export const LabAnalysis: React.FC<LabAnalysisProps> = ({ isAuthenticated }) => {
  const [formData, setFormData] = useState({
    chemo: '',
    troponin: '',
    bnp: '',
    lvef: '',
    ecg: ''
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [interpretation, setInterpretation] = useState('');

  const handleGenerate = async () => {
    if (!isAuthenticated) {
      alert("Session unauthenticated. Please log in.");
      return;
    }
    
    // Use the injected API key if available, otherwise use a placeholder
    const apiKey = process.env.GEMINI_API_KEY || "MY_GEMINI_API_KEY";
    
    setIsGenerating(true);
    try {
      const ai = new GoogleGenAI({ apiKey });
      const prompt = `Act as an expert Cardio-Oncologist AI. Interpret the following lab and diagnostic data for a patient undergoing cancer treatment. Provide a structured clinical interpretation, risk grade (Low, Moderate, High, Critical), and recommended next steps.
      
      Data:
      - Chemotherapy/Treatment: ${formData.chemo || 'Not provided'}
      - Troponin (ng/L): ${formData.troponin || 'Not provided'}
      - BNP/NT-proBNP (pg/mL): ${formData.bnp || 'Not provided'}
      - LVEF (%): ${formData.lvef || 'Not provided'}
      - ECG Summary: ${formData.ecg || 'Not provided'}
      `;
      
      const response = await ai.models.generateContent({
        model: 'gemini-3.1-pro-preview',
        contents: prompt,
      });
      
      setInterpretation(response.text || 'No interpretation generated.');
    } catch (error: any) {
      console.error("Error generating interpretation:", error);
      setInterpretation(`An error occurred: ${error.message || "Unknown error"}`);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <header className="flex flex-col md:flex-row md:justify-between md:items-end gap-4 mb-8">
        <div>
          <div className="flex items-center gap-2 text-sm text-slate-400 mb-2 font-medium">
            <span>Cardio-Onc AI</span>
            <span>/</span>
            <span className="text-raspberry-500 uppercase">Labs</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">Lab & Diagnostic Center</h2>
          <p className="text-slate-500 text-sm md:text-base">Submit findings for AI interpretation and governed risk grading.</p>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
        {/* Input Form */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8 shadow-sm">
          <h3 className="font-bold text-raspberry-700 mb-6 flex items-center gap-2">
            <FileText size={20} /> Input Clinical Data
          </h3>
          
          <div className="space-y-4 md:space-y-5">
            <div>
              <label className="block text-xs md:text-sm font-medium text-slate-700 mb-1.5">Chemotherapy / Cancer Treatment</label>
              <input 
                type="text" 
                value={formData.chemo}
                onChange={(e) => setFormData({...formData, chemo: e.target.value})}
                placeholder="e.g. Anthracyclines, Trastuzumab, ICI" 
                className="w-full border border-slate-300 rounded-lg px-4 py-2.5 text-slate-900 text-sm md:text-base focus:ring-2 focus:ring-raspberry-500 focus:border-raspberry-500 outline-none transition-all" 
              />
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs md:text-sm font-medium text-slate-700 mb-1.5">Troponin (ng/L)</label>
                <input 
                  type="text" 
                  value={formData.troponin}
                  onChange={(e) => setFormData({...formData, troponin: e.target.value})}
                  placeholder="Value" 
                  className="w-full border border-slate-300 rounded-lg px-4 py-2.5 text-slate-900 text-sm md:text-base focus:ring-2 focus:ring-raspberry-500 focus:border-raspberry-500 outline-none transition-all" 
                />
              </div>
              <div>
                <label className="block text-xs md:text-sm font-medium text-slate-700 mb-1.5">BNP / NT-proBNP (pg/mL)</label>
                <input 
                  type="text" 
                  value={formData.bnp}
                  onChange={(e) => setFormData({...formData, bnp: e.target.value})}
                  placeholder="Value" 
                  className="w-full border border-slate-300 rounded-lg px-4 py-2.5 text-slate-900 text-sm md:text-base focus:ring-2 focus:ring-raspberry-500 focus:border-raspberry-500 outline-none transition-all" 
                />
              </div>
            </div>

            <div>
              <label className="block text-xs md:text-sm font-medium text-slate-700 mb-1.5">Echocardiogram LVEF (%)</label>
              <input 
                type="text" 
                value={formData.lvef}
                onChange={(e) => setFormData({...formData, lvef: e.target.value})}
                placeholder="e.g. 55%" 
                className="w-full border border-slate-300 rounded-lg px-4 py-2.5 text-slate-900 text-sm md:text-base focus:ring-2 focus:ring-raspberry-500 focus:border-raspberry-500 outline-none transition-all" 
              />
            </div>

            <div>
              <label className="block text-xs md:text-sm font-medium text-slate-700 mb-1.5">ECG Clinical Summary</label>
              <textarea 
                rows={4} 
                value={formData.ecg}
                onChange={(e) => setFormData({...formData, ecg: e.target.value})}
                placeholder="Describe arrhythmias, QTc intervals, etc." 
                className="w-full border border-slate-300 rounded-lg px-4 py-2.5 text-slate-900 text-sm md:text-base focus:ring-2 focus:ring-raspberry-500 focus:border-raspberry-500 outline-none transition-all resize-none"
              ></textarea>
            </div>

            <button 
              onClick={handleGenerate}
              disabled={isGenerating}
              className="w-full bg-raspberry-600 hover:bg-raspberry-700 disabled:bg-raspberry-400 text-white px-6 py-3.5 rounded-xl font-bold transition-colors shadow-sm flex items-center justify-center gap-2 mt-4"
            >
              {isGenerating ? (
                <><Activity size={20} className="animate-spin" /> Analyzing Data...</>
              ) : (
                <><FileText size={20} /> Generate Clinical Interpretation</>
              )}
            </button>
          </div>
        </div>

        {/* AI Insight Engine */}
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm flex flex-col overflow-hidden h-[500px] md:h-[650px]">
          <div className="p-4 border-b border-slate-100 bg-slate-50 flex items-center gap-2 shrink-0">
            <ShieldCheck size={20} className="text-emerald-600" />
            <h3 className="font-bold text-slate-900">AI Insight Engine</h3>
          </div>
          
          <div className="flex-1 overflow-y-auto p-4 md:p-6">
            {!interpretation && !isGenerating ? (
              <div className="h-full flex flex-col items-center justify-center text-center">
                <div className="w-12 h-12 md:w-16 md:h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                  <Send size={24} className="text-slate-400 -ml-1 mt-1" />
                </div>
                <p className="text-slate-500 text-sm">Submit lab data on the left<br/>to receive AI interpretation.</p>
              </div>
            ) : isGenerating ? (
              <div className="h-full flex flex-col items-center justify-center text-center space-y-4">
                <Activity size={32} className="text-raspberry-500 animate-spin" />
                <p className="text-raspberry-600 font-medium animate-pulse text-sm">Running cardio-oncology risk models...</p>
              </div>
            ) : (
              <div className="markdown-body text-xs md:text-sm text-slate-700">
                <ReactMarkdown>{interpretation}</ReactMarkdown>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
